// src/components/LifecycleEventForm.js

import React, { useState, useRef } from 'react';
import { Loader, CheckCircle, AlertTriangle, Upload, FileText } from 'lucide-react';

const PRIMARY = '#326BDF';

const REPAIR_STATUSES    = ['In Progress', 'Completed', 'Cannot Repair'];
const DISASSEMBLY_STAGES = ['Received', 'Disassembling', 'Components Sorted', 'Complete'];
const DISPOSAL_METHODS   = ['Mechanical', 'Chemical', 'Manual'];

const API_BASE_URL = 'https://dpp-api.acespireconsulting.com/api';

// ── PDF Upload Field ──────────────────────────────────────────────────────────
function PdfUploadField({ pdfFile, onChange }) {
  const fileInputRef = useRef(null);

  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
        Attach PDF Report <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(optional)</span>
      </label>
      <div
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `1.5px dashed #BFDBFE`,
          borderRadius: 8, padding: '12px 16px',
          backgroundColor: '#EFF6FF',
          cursor: 'pointer', textAlign: 'center',
          transition: 'all 0.15s',
        }}
      >
        {pdfFile ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <FileText size={16} color={PRIMARY} />
            <span style={{ fontSize: 13, color: PRIMARY, fontWeight: 600 }}>{pdfFile.name}</span>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Upload size={16} color={PRIMARY} />
            <span style={{ fontSize: 13, color: PRIMARY }}>Click to upload PDF</span>
          </div>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        onChange={(e) => onChange(e.target.files[0] || null)}
        style={{ display: 'none' }}
      />
    </div>
  );
}

// ── Repairer Form ─────────────────────────────────────────────────────────────
function RepairerForm({ dppId, onSuccess }) {
  const [component, setComponent] = useState('');
  const [status,    setStatus]    = useState('');
  const [notes,     setNotes]     = useState('');
  const [pdfFile,   setPdfFile]   = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);

  const handleSubmit = async () => {
    if (!component || !status) { setError('Please fill all required fields.'); return; }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('id_token') || localStorage.getItem('access_token') || localStorage.getItem('authToken');

      const formData = new FormData();
      formData.append('event_type', 'Repair');
      formData.append('event_data', JSON.stringify({
        component,
        repair_status: status,
        notes: notes.trim() || undefined,
      }));
      if (pdfFile) formData.append('file', pdfFile);

      const res = await fetch(`${API_BASE_URL}/lifecycle-event/${dppId}`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}` },
        body:    formData,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to submit event');

      setComponent(''); setStatus(''); setNotes(''); setPdfFile(null);
      onSuccess('Repair event submitted successfully!');
    } catch (err) {
      setError(err.message || 'Failed to submit event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#B91C1C', display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={14} />
          {error}
        </div>
      )}

      {/* Component */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
          Component Being Repaired <span style={{ color: '#EF4444' }}>*</span>
        </label>
        <input
          type="text"
          value={component}
          onChange={e => setComponent(e.target.value)}
          placeholder="e.g. Display Panel, Power Board, Speaker..."
          style={{
            width: '100%', padding: '9px 12px', borderRadius: 8,
            border: `1.5px solid ${component ? PRIMARY : '#D1D5DB'}`,
            fontSize: 13, color: '#111827', outline: 'none',
            boxSizing: 'border-box', fontFamily: 'inherit'
          }}
          onFocus={e => e.target.style.borderColor = PRIMARY}
          onBlur={e => e.target.style.borderColor = component ? PRIMARY : '#D1D5DB'}
        />
      </div>

      {/* Repair Status */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
          Repair Status <span style={{ color: '#EF4444' }}>*</span>
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {REPAIR_STATUSES.map(s => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              style={{
                padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                border: `1.5px solid ${status === s ? PRIMARY : '#D1D5DB'}`,
                backgroundColor: status === s ? `${PRIMARY}15` : '#fff',
                color: status === s ? PRIMARY : '#6B7280',
                cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
          Technician Notes <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(optional)</span>
        </label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Describe the issue or observations..."
          rows={3}
          style={{
            width: '100%', padding: '9px 12px', borderRadius: 8,
            border: '1.5px solid #D1D5DB', fontSize: 13, color: '#111827',
            outline: 'none', resize: 'vertical', fontFamily: 'inherit',
            boxSizing: 'border-box'
          }}
          onFocus={e => e.target.style.borderColor = PRIMARY}
          onBlur={e => e.target.style.borderColor = '#D1D5DB'}
        />
      </div>

      {/* PDF Upload */}
      <PdfUploadField pdfFile={pdfFile} onChange={setPdfFile} />

      <button
        onClick={handleSubmit}
        disabled={loading}
        style={{
          padding: '10px 24px', borderRadius: 8, border: 'none',
          backgroundColor: loading ? '#93C5FD' : PRIMARY,
          color: '#fff', fontSize: 13, fontWeight: 600,
          cursor: loading ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', gap: 8,
          alignSelf: 'flex-start', transition: 'background 0.15s'
        }}
      >
        {loading
          ? <><Loader size={14} className="animate-spin" />Submitting...</>
          : 'Submit Repair Event'
        }
      </button>
    </div>
  );
}

// ── Recycler Form ─────────────────────────────────────────────────────────────
function RecyclerForm({ dppId, onSuccess }) {
  const [stage,          setStage]         = useState('');
  const [materials,      setMaterials]     = useState('');
  const [disposalMethod, setDisposalMethod] = useState('');
  const [pdfFile,        setPdfFile]       = useState(null);
  const [loading,        setLoading]       = useState(false);
  const [error,          setError]         = useState(null);

  const handleSubmit = async () => {
    if (!stage || !disposalMethod) { setError('Please fill all required fields.'); return; }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('id_token') || localStorage.getItem('access_token') || localStorage.getItem('authToken');

      const formData = new FormData();
      formData.append('event_type', 'Recycle');
      formData.append('event_data', JSON.stringify({
        disassembly_stage:   stage,
        materials_recovered: materials.trim() || undefined,
        disposal_method:     disposalMethod,
      }));
      if (pdfFile) formData.append('file', pdfFile);

      const res = await fetch(`${API_BASE_URL}/lifecycle-event/${dppId}`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}` },
        body:    formData,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to submit event');

      setStage(''); setMaterials(''); setDisposalMethod(''); setPdfFile(null);
      onSuccess('Recycling event submitted successfully!');
    } catch (err) {
      setError(err.message || 'Failed to submit event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#B91C1C', display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={14} />
          {error}
        </div>
      )}

      {/* Disassembly Stage */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
          Disassembly Stage <span style={{ color: '#EF4444' }}>*</span>
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {DISASSEMBLY_STAGES.map(s => (
            <button
              key={s}
              onClick={() => setStage(s)}
              style={{
                padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                border: `1.5px solid ${stage === s ? PRIMARY : '#D1D5DB'}`,
                backgroundColor: stage === s ? `${PRIMARY}15` : '#fff',
                color: stage === s ? PRIMARY : '#6B7280',
                cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Materials Recovered */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
          Materials Recovered <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(optional)</span>
        </label>
        <input
          type="text"
          value={materials}
          onChange={e => setMaterials(e.target.value)}
          placeholder="e.g. Copper, Plastic, Glass, Rare Earth Metals..."
          style={{
            width: '100%', padding: '9px 12px', borderRadius: 8,
            border: '1.5px solid #D1D5DB', fontSize: 13, color: '#111827',
            outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit'
          }}
          onFocus={e => e.target.style.borderColor = PRIMARY}
          onBlur={e => e.target.style.borderColor = '#D1D5DB'}
        />
      </div>

      {/* Disposal Method */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>
          Disposal Method <span style={{ color: '#EF4444' }}>*</span>
        </label>
        <select
          value={disposalMethod}
          onChange={e => setDisposalMethod(e.target.value)}
          style={{
            width: '100%', padding: '9px 12px', borderRadius: 8,
            border: `1.5px solid ${disposalMethod ? PRIMARY : '#D1D5DB'}`,
            fontSize: 13, color: '#111827', outline: 'none',
            backgroundColor: '#fff', cursor: 'pointer'
          }}
        >
          <option value="">Select method...</option>
          {DISPOSAL_METHODS.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      {/* PDF Upload */}
      <PdfUploadField pdfFile={pdfFile} onChange={setPdfFile} />

      <button
        onClick={handleSubmit}
        disabled={loading}
        style={{
          padding: '10px 24px', borderRadius: 8, border: 'none',
          backgroundColor: loading ? '#93C5FD' : PRIMARY,
          color: '#fff', fontSize: 13, fontWeight: 600,
          cursor: loading ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', gap: 8,
          alignSelf: 'flex-start', transition: 'background 0.15s'
        }}
      >
        {loading
          ? <><Loader size={14} className="animate-spin" />Submitting...</>
          : 'Submit Recycling Event'
        }
      </button>
    </div>
  );
}

// ── Main LifecycleEventForm ───────────────────────────────────────────────────
function LifecycleEventForm({ dppId, userRole, onSubmitSuccess }) {
  const [successMsg, setSuccessMsg] = useState(null);

  const handleSuccess = (msg) => {
    setSuccessMsg(msg);
    if (onSubmitSuccess) {
      setTimeout(() => onSubmitSuccess(), 1500);
    } else {
      setTimeout(() => setSuccessMsg(null), 4000);
    }
  };

  const isRepairer = userRole === 'Repair/Refurb';
  const isRecycler = userRole === 'Recycler';

  if (!isRepairer && !isRecycler) return null;

  return (
    <div style={{
      marginTop: 8,
      background: '#fff',
      border: '1px solid #E2EAF8',
      borderRadius: 12,
      padding: 24,
      boxShadow: '0 1px 4px rgba(50,107,223,0.06)',
    }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>
          {isRepairer ? 'Log Repair Event' : 'Log Recycling Event'}
        </h3>
        <p style={{ fontSize: 12, color: '#6B7280', margin: 0 }}>
          {isRepairer
            ? 'Record what component you are working on and the current repair status.'
            : 'Record the current disassembly stage and materials recovered.'}
        </p>
      </div>

      {/* Success message */}
      {successMsg && (
        <div style={{
          background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8,
          padding: '10px 14px', fontSize: 13, color: '#15803D',
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16
        }}>
          <CheckCircle size={14} />
          {successMsg}
        </div>
      )}

      {/* Form */}
      {isRepairer && (
        <RepairerForm dppId={dppId} onSuccess={handleSuccess} />
      )}
      {isRecycler && (
        <RecyclerForm dppId={dppId} onSuccess={handleSuccess} />
      )}
    </div>
  );
}

export default LifecycleEventForm;